import React from 'react';
import { BarChart3 } from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler
);

const LiveStreamAnalyticsCharts = ({ analyticsData, event }) => {
    // If no analytics data is available, show a message instead of mock data
    if (!analyticsData || !analyticsData.viewerMetrics) {
        return (
            <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">No Chart Data Available</h4>
                <p className="text-gray-500">
                    Chart visualizations will appear here when analytics data becomes available.
                </p>
            </div>
        );
    }
    // Sample data for charts when real data is not available
    const viewerTrendData = {
        labels: ['00:00', '00:05', '00:10', '00:15', '00:20', '00:25', '00:30', '00:35', '00:40', '00:45', '00:50', '00:55', '01:00'],
        datasets: [
            {
                label: 'Live Viewers',
                data: [45, 52, 78, 125, 156, 189, 234, 267, 245, 223, 198, 176, 156],
                borderColor: '#3B82F6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                fill: true,
                tension: 0.4
            }
        ]
    };

    const deviceData = {
        labels: Object.keys(analyticsData?.demographics?.deviceTypes || {
            'Mobile': 70,
            'Desktop': 25,
            'Tablet': 5
        }),
        datasets: [
            {
                data: Object.values(analyticsData?.demographics?.deviceTypes || {
                    'Mobile': 70,
                    'Desktop': 25,
                    'Tablet': 5
                }),
                backgroundColor: ['#3B82F6', '#10B981', '#F59E0B'],
                borderWidth: 2,
                borderColor: '#ffffff'
            }
        ]
    };

    const ageGroupData = {
        labels: Object.keys(analyticsData?.demographics?.ageGroups || {
            '18-25': 20,
            '26-35': 35,
            '36-45': 30,
            '46+': 15
        }),
        datasets: [
            {
                label: 'Percentage',
                data: Object.values(analyticsData?.demographics?.ageGroups || {
                    '18-25': 20,
                    '26-35': 35,
                    '36-45': 30,
                    '46+': 15
                }),
                backgroundColor: '#10B981',
                borderRadius: 4
            }
        ]
    };

    const topCitiesData = {
        labels: ['Mumbai', 'Delhi', 'Bangalore', 'Pune', 'Chennai'],
        datasets: [
            {
                label: 'Viewers',
                data: [85, 72, 64, 58, 45],
                backgroundColor: '#F59E0B',
                borderRadius: 4
            }
        ]
    };

    const engagementData = {
        labels: ['Join Rate', 'Completion Rate', 'Return Rate', 'Interaction Rate'],
        datasets: [
            {
                label: 'Percentage',
                data: [
                    parseFloat(analyticsData?.engagement?.joinRate || '75'),
                    100 - parseFloat(analyticsData?.engagement?.dropOffRate || '25'),
                    parseFloat(analyticsData?.engagement?.returningViewers || '35'),
                    65
                ],
                backgroundColor: '#8B5CF6',
                borderRadius: 4
            }
        ]
    };

    const revenueData = {
        labels: ['1 day ago', '6 hrs ago', '3 hrs ago', '1 hr ago', 'Stream start', 'Now'],
        datasets: [
            {
                label: 'Revenue (₹)',
                data: [0, 15000, 28000, 35000, 42000, analyticsData?.financialMetrics?.totalRevenue || 45000],
                borderColor: '#10B981',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#10B981',
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 6
            }
        ]
    };

    const watchTimeData = {
        labels: ['< 5 min', '5-15 min', '15-30 min', '30-45 min', '> 45 min'],
        datasets: [
            {
                label: 'Viewers',
                data: [45, 78, 123, 67, 34],
                backgroundColor: '#EC4899',
                borderRadius: 4
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top'
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#374151',
                borderWidth: 1
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            x: {
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom'
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff'
            }
        }
    };

    return (
        <div className="space-y-8">
            {/* Viewer Trend Chart */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Viewer Trend During Stream</h4>
                <div className="h-80">
                    <Line data={viewerTrendData} options={chartOptions} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Device Distribution */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Device Usage</h4>
                    <div className="h-64">
                        <Doughnut data={deviceData} options={pieOptions} />
                    </div>
                    <div className="mt-4 space-y-2">
                        {Object.entries(analyticsData?.demographics?.deviceTypes || {
                            'Mobile': 70,
                            'Desktop': 25,
                            'Tablet': 5
                        }).map(([device, percentage], index) => (
                            <div key={device} className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div 
                                        className="w-3 h-3 rounded-full mr-2"
                                        style={{ backgroundColor: ['#3B82F6', '#10B981', '#F59E0B'][index] }}
                                    ></div>
                                    <span className="text-sm text-gray-600">{device}</span>
                                </div>
                                <span className="text-sm font-medium">
                                    {percentage}% ({Math.floor((percentage / 100) * (analyticsData?.viewerMetrics?.totalViewers || 300))})
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Age Distribution */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Age Distribution</h4>
                    <div className="h-64">
                        <Bar data={ageGroupData} options={chartOptions} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Cities */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Top Cities</h4>
                    <div className="h-64">
                        <Bar 
                            data={topCitiesData} 
                            options={{
                                ...chartOptions,
                                indexAxis: 'y'
                            }} 
                        />
                    </div>
                </div>

                {/* Engagement Metrics */}
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Engagement Rates</h4>
                    <div className="h-64">
                        <Bar data={engagementData} options={chartOptions} />
                    </div>
                </div>
            </div>

            {/* Revenue Trend (for paid events) */}
            {event?.accessType === 'PAID' && (
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Revenue Timeline</h4>
                    <div className="h-64">
                        <Line data={revenueData} options={chartOptions} />
                    </div>
                </div>
            )}

            {/* Watch Time Distribution */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Watch Time Distribution</h4>
                <div className="h-64">
                    <Bar data={watchTimeData} options={chartOptions} />
                </div>
            </div>
        </div>
    );
};

export default LiveStreamAnalyticsCharts;